import { Box, Skeleton } from '@chakra-ui/react';
import Script from 'next/script';
import { FC, ReactNode } from 'react';
import { VideoObject, WithContext } from 'schema-dts';

import { useLecturePageQueryParams } from '@/components/ui/features/lecture/hooks';
import { useLecturePlayerContext } from '@/components/ui/features/lecture/LecturePlayer/LecturePlayer.context';
import {
  useLectureVideoAnalytics,
  useTrackOptions,
} from '@/components/ui/features/lecture/LecturePlayer/LecturePlayer.hooks';
import { LecturePlayerLanguageSelection } from '@/components/ui/features/lecture/LecturePlayer/LecturePlayerLanguageSelection';
import { VideoJS } from '@/components/ui/shared/video/VideoJS/VideoJS';
import { defaultVideoType } from '@/constants/video';
import { queryThinLectureWithTagAndEvent } from '@/fetchers/thinLectures';
import { useResource, useResources } from '@/lib/datx-jsonapi-react';
import { queryTranscriptsByLectureId } from '@/queries/transcripts';

interface ILecturePlayerVideoProps {
  leftAction?: ReactNode;
  rightAction?: ReactNode;
}

const peerHoverStyle = {
  opacity: '0',
  _hover: { opacity: 1 },
  _peerHover: { opacity: 1 },
  transition: 'opacity 0.2s',
};

export const LecturePlayerVideo: FC<ILecturePlayerVideoProps> = ({ leftAction, rightAction }) => {
  const { id: lectureId } = useLecturePageQueryParams();
  const [_, update] = useLecturePlayerContext();
  const { data: lecture } = useResource(queryThinLectureWithTagAndEvent(lectureId));
  const { data: transcripts } = useResources(queryTranscriptsByLectureId(lectureId));
  const trackOptions = useTrackOptions({ transcripts, activeTrackId: null });
  const isLoading = !lecture || !transcripts;

  const videoEvents = useLectureVideoAnalytics();

  if (isLoading) {
    return <Skeleton w="full" h={{ base: '300px', lg: '400px' }} />;
  }

  const fallbackThumbnailUrl = '/images/landing/conferences-gradient.png';

  const posterThumbnail = lecture.mediumPosterDocumentThumbnailUrl || lecture.mediumPosterDocumentThumbnailExtractUrl;

  const imageSrc = lecture.posterLecture
    ? posterThumbnail || lecture.mediumPosterUrl || lecture.event.mediumPosterFallbackImageUrl
    : lecture.mediumPosterUrl || posterThumbnail || lecture.event.mediumVideoFallbackImageUrl;

  const imgWithFallback = imageSrc || fallbackThumbnailUrl;

  const structuredData: WithContext<VideoObject> = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: lecture.title,
    description: lecture.synopsis,
    thumbnailUrl: imgWithFallback,
    uploadDate: lecture.publishedAt,
    contentUrl: lecture.playlist,
  };

  return (
    <Box h="full" position="relative">
      <Script
        strategy="beforeInteractive"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <VideoJS
        data-peer
        key={lectureId}
        title={lecture.title}
        options={{
          sources: [{ src: lecture.playlist, type: defaultVideoType }],
          tracks: trackOptions,
          poster: imgWithFallback,
        }}
        crossOrigin="anonymous"
        onReady={update}
        {...videoEvents}
      />
      <Box {...peerHoverStyle} position="absolute" top="10px" left="10px" zIndex="1">
        {leftAction}
      </Box>
      <LecturePlayerLanguageSelection>{rightAction}</LecturePlayerLanguageSelection>
    </Box>
  );
};
